import { useCallback, useState } from 'react';

import type { ScheduleViewMode } from './types';

export interface ScheduleState {
  viewMode: ScheduleViewMode;
  selectedMemberId: string | null;
  /** True while a member filter is active — the view dropdown is disabled. */
  isViewLocked: boolean;
  setViewMode: (mode: ScheduleViewMode) => void;
  /** Filter to a single member; forces List view and locks the view dropdown. */
  selectMember: (memberId: string) => void;
  /** "See All" — clears the member filter and re-enables the view dropdown. */
  clearMember: () => void;
}

// Encapsulates the coach Schedule interaction rules (MEN-4160):
// - Default view is Week.
// - Selecting a member filters to that member, forces List view, and disables
//   the view dropdown.
// - "See All" clears the filter and unlocks the dropdown.
export const useScheduleState = (): ScheduleState => {
  const [viewMode, setViewMode] = useState<ScheduleViewMode>('week');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  const isViewLocked = selectedMemberId !== null;

  const changeViewMode = useCallback(
    (mode: ScheduleViewMode) => {
      // The dropdown is disabled while filtered; ignore stray changes defensively.
      if (selectedMemberId !== null) return;
      setViewMode(mode);
    },
    [selectedMemberId],
  );

  const selectMember = useCallback((memberId: string) => {
    setSelectedMemberId(memberId);
    setViewMode('list');
  }, []);

  const clearMember = useCallback(() => {
    setSelectedMemberId(null);
  }, []);

  return {
    clearMember,
    isViewLocked,
    selectMember,
    selectedMemberId,
    setViewMode: changeViewMode,
    viewMode,
  };
};
